import { useState, useCallback, useMemo } from 'react';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook central pour la gestion des invités d'un plan de table.
 * Réutilisable depuis la page invités ET depuis l'éditeur (via PlanContext).
 */
export function useInvites(planToken, authToken) {
  const [plan,     setPlan]    = useState(null);
  const [invites,  setInvites] = useState([]);
  const [loading,  setLoading] = useState(true);
  const [error,    setError]   = useState(null);

  const headers = useMemo(
    () => ({ Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }),
    [authToken]
  );

  // ─ Fetch complet (plan + invités) ──────────────────────────────────────────
  const fetchAll = useCallback(async () => {
    if (!planToken || !authToken) return;
    setLoading(true);
    setError(null);
    try {
      const res  = await fetch(`${BACKEND}/api/plan-de-table/${planToken}`, { headers });
      const data = await res.json();
      setPlan(data.plan);
      setInvites(data.plan?.invites ?? []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [planToken, authToken]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─ CRUD invités ──────────────────────────────────────────────────────────
  const createInvite = useCallback(async (data) => {
    const res = await fetch(`${BACKEND}/api/plan-de-table/${planToken}/invites`,
      { method: 'POST', headers, body: JSON.stringify(data) });
    if (!res.ok) throw new Error(await res.text());
    await fetchAll();
    return res.json();
  }, [planToken, headers, fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateInvite = useCallback(async (id, data) => {
    const res = await fetch(`${BACKEND}/api/plan-de-table/${planToken}/invites/${id}`,
      { method: 'PATCH', headers, body: JSON.stringify(data) });
    if (!res.ok) throw new Error(await res.text());
    await fetchAll();
  }, [planToken, headers, fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const deleteInvite = useCallback(async (id) => {
    const res = await fetch(`${BACKEND}/api/plan-de-table/${planToken}/invites/${id}`,
      { method: 'DELETE', headers });
    if (!res.ok) throw new Error(await res.text());
    await fetchAll();
  }, [planToken, headers, fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const cycleStatus = useCallback(async (inviteId, segId) => {
    // Optimistic update
    const CYCLE = ['pending','yes','no','not_invited'];
    setInvites(prev => prev.map(inv => {
      if (inv.id !== inviteId) return inv;
      const cur  = inv.seg_status?.[segId] ?? 'pending';
      const next = CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length];
      return { ...inv, seg_status: { ...inv.seg_status, [segId]: next } };
    }));
    // Commit server
    try {
      await fetch(
        `${BACKEND}/api/plan-de-table/${planToken}/invites/${inviteId}/cycle-status`,
        { method: 'POST', headers, body: JSON.stringify({ seg_id: segId }) },
      );
      // Re-sync silencieux (pas de setLoading pour éviter le flash)
      const res  = await fetch(`${BACKEND}/api/plan-de-table/${planToken}`);
      const data = await res.json();
      setInvites(data.plan?.invites ?? []);
    } catch { /* rollback sur prochain fetch */ }
  }, [planToken, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  const bulkImport = useCallback(async (list) => {
    const res = await fetch(
      `${BACKEND}/api/plan-de-table/${planToken}/invites/bulk-import`,
      { method: 'POST', headers, body: JSON.stringify({ invites: list }) },
    );
    if (!res.ok) throw new Error(await res.text());
    const data = await res.json();
    await fetchAll();
    return data.added ?? list.length;
  }, [planToken, headers, fetchAll]); // eslint-disable-line react-hooks/exhaustive-deps

  const exportCSV = useCallback(async (eventName = 'invites') => {
    const res = await fetch(
      `${BACKEND}/api/plan-de-table/${planToken}/export-invites.csv`,
      { headers },
    );
    if (!res.ok) throw new Error('Export échoué');
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `invites-${eventName.replace(/\s+/g, '-').toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [planToken, headers]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    plan, invites, loading, error,
    groups:   plan?.groups   ?? [],
    segments: plan?.segments ?? [],
    mainSeg:  (plan?.segments ?? []).find(s => s.has_plan) ?? (plan?.segments ?? [])[0] ?? null,
    fetchAll, createInvite, updateInvite, deleteInvite,
    cycleStatus, bulkImport, exportCSV,
  };
}
